import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { CATEGORIES, withCanonicalNames } from '../constants/categories';
import type { Category } from '../constants/categories';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { dedupeInit, isCloudFresh, markCloudFresh } from '../lib/cloudBoot';
import { accountGeneration, accountStorage, cachedUserIdOwner, scopeToAccount } from '../lib/accountStorage';

// ─── Supabase sync (sync epic) ──────────────────────────────────────────────
// The 7 domains are a fixed, user-customizable set (no add/delete), so this
// syncs as ONE row per user (user_categories.user_id → categories jsonb), not
// row-per-item. Cloud is the source of truth on load. Writes fire-and-forget.

const USER_CATEGORIES_TABLE = 'user_categories';

async function cloudUpsertCategories(categories: Category[]): Promise<void> {
  try {
    const { user } = await syncManager.safeAuthCheck();
    if (!user) return;
    const { error } = await supabase
      .from(USER_CATEGORIES_TABLE)
      .upsert(
        { user_id: user.id, categories, updated_at: new Date().toISOString() },
        { onConflict: 'user_id' }
      );
    if (error) console.error('Failed to sync categories to Supabase:', error);
  } catch (error) {
    console.error('Error syncing categories to Supabase:', error);
  }
}

interface CategoryState {
  categories: Category[];
  initializedUserId: string | null;
  cachedUserId: string | null;
  initialize: () => Promise<void>;
  updateCategory: (id: string, updates: Partial<Category>) => void;
  resetCategories: () => void;
  saveChanges: () => Promise<void>;
}

export const useCategoryStore = create<CategoryState>()(
  persist<CategoryState, [], [], any>(
    (set, get) => ({
      categories: CATEGORIES,
      initializedUserId: null,
      cachedUserId: null,

      // Load-on-login, keyed by user id (re-runs on account switch). Cloud row
      // is authoritative if present; otherwise keep this user's local edits (or
      // reset to defaults when the cache belongs to a different account).
      initialize: () => dedupeInit('user_categories', async () => {
        const at = accountGeneration();
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return;
          if (get().initializedUserId === user.id) return;
          // Cache-first boot: while this user's local cache is fresh, skip the
          // cloud read entirely (write-through keeps cloud current meanwhile).
          if (get().cachedUserId === user.id && isCloudFresh('user_categories', user.id)) {
            set({ initializedUserId: user.id });
            return;
          }

          const { data, error } = await supabase
            .from(USER_CATEGORIES_TABLE)
            .select('*');
          // An answer for an account that has since signed out is not this copy's.
          if (at !== accountGeneration()) return;
          if (error) throw error;

          const row = (data ?? [])[0];
          set((state) => {
            if (row && Array.isArray(row.categories) && row.categories.length > 0) {
              return {
                /**
                 * ⚠️⚠️ **NAMES COME FROM THE CONSTANT, NOT FROM THE ROW.** The
                 * cloud row is authoritative for what the USER owns — colour and
                 * icon — but not for the domain's NAME, which this app owns.
                 * Rows written before the name editor was removed still carry
                 * whatever was typed, and one of them would otherwise mask the
                 * owner's own "Ecological" → "Material" rename forever.
                 */
                categories: withCanonicalNames(row.categories as Category[]),
                initializedUserId: user.id,
                cachedUserId: user.id,
              };
            }
            const cacheIsOurs =
              state.cachedUserId === null || state.cachedUserId === user.id;
            return {
              categories: cacheIsOurs ? withCanonicalNames(state.categories) : CATEGORIES,
              initializedUserId: user.id,
              cachedUserId: user.id,
            };
          });
          markCloudFresh('user_categories', user.id);
        } catch (error) {
          console.error('Failed to initialize categories from Supabase:', error);
        }
      }),

      updateCategory: (id, updates) => {
        set((state) => {
          const newCategories = state.categories.map((category) =>
            category.id === id
              ? { ...category, ...updates }
              : category
          );

          return { categories: newCategories };
        });

        void cloudUpsertCategories(get().categories);
      },

      resetCategories: () => {
        set({ categories: CATEGORIES });
        void cloudUpsertCategories(CATEGORIES);
      },

      saveChanges: async () => {
        // Persist the current customizations to the cloud (explicit Save).
        await cloudUpsertCategories(get().categories);
      }
    }),
    {
      name: 'category-storage',
      // ⭐ One saved copy per account — see `scopeToAccount` below.
      storage: accountStorage(cachedUserIdOwner),
      version: 2,
      // `initializedUserId` stays transient; persist categories + cache owner.
      partialize: (state: CategoryState) => ({
        categories: state.categories,
        cachedUserId: state.cachedUserId,
      }),
      migrate: (persistedState: any, version: number) => {
        if (version < 2) {
          // Reset to default categories if version is old
          return { categories: CATEGORIES } as unknown as CategoryState;
        }
        /**
         * ⚠️ The LOCAL cache needs the same normalisation as the cloud row —
         * it is read before `initialize()` resolves, so without this a renamed
         * domain would flash its old custom name on every cold boot even once
         * the cloud path was fixed.
         * ⚠️ Not a version bump: this is not a shape change, and bumping would
         * throw away the user's colours to fix their names.
         */
        const s = persistedState as CategoryState;
        return (Array.isArray(s?.categories)
          ? { ...s, categories: withCanonicalNames(s.categories) }
          : s) as CategoryState;
      }
    }
  )
);

/* ⚠️⚠️ ONE SAVED COPY PER ACCOUNT (lib/accountStorage). The device kept one copy, tagged with the
   account it was loaded for, and a sign-out does not reload the page: the next account saw the last
   one's colours and icons until its own load, and its first edit before that load saved them — the
   whole set is written — into its own row; an account's edit whose write never landed was replaced by
   the next account's load; and the next account's edit made before its own load was kept by the last
   one's load when its row had none. */
scopeToAccount(useCategoryStore);
